/**
 * Route copy for `src/routes/mediaLibrary` (keys: `routes.mediaLibrary.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  assetDetailModal: {
    draft: 'draft',
    metadataSaved: 'Metadata saved.',
    savingFailed: 'Saving failed.',
    mediaDeleted: 'Media deleted.',
    deletionFailed: 'Deletion failed.',
    urlCopied: 'URL copied.',
    couldNotCopy: 'Could not copy.',
    mediaDetails: 'Media details',
    deletePermanently: 'Delete permanently?',
    cancel: 'Cancel',
    delete: 'Delete',
    close: 'Close',
    save: 'Save',
    mimeType: 'MIME type',
    identifier: 'Identifier',
    project: 'Project',
    copyTheUrl: 'Copy the URL',
    open: 'Open',
    download: 'Download',
    fileName: 'File name',
    folderCollection: 'Folder / collection',
    none: 'None',
    tagsSeparatedBy: 'Tags (separated by ,)',
    altText: 'Alt text',
    usage: 'Usage',
    lookingUpLinkedContent: 'Looking up linked content…',
    couldNotLoadTheUsageRight: 'Could not load the usage right now. Try again later.',
    thisMediaIsNotUsedIn: 'This media is not used in any content, design or post. The folders and tags above are for organization.',
    model: 'Model',
    inReview: 'in review',
    approved: 'approved',
    scheduled: 'scheduled',
    published: 'published',
    failed: 'failed',
    archived: 'archived',
    untitledId: '(untitled — {{id}})',
    preview: 'preview',
    content: 'Content',
    design: 'Design',
  },
  generateImageModal: {
    vibrant: 'Vibrant',
    queued: 'Queued…',
    generating: 'Generating…',
    couldNotCheckTheAvailabilityOf: 'Could not check the availability of the generation engines. Images stay available; video / avatar may be too.',
    theModelReturnedImageDescriptionsBut: 'The model returned image descriptions but no usable image.',
    noImageWasGeneratedTryAgain: 'No image was generated. Try again with a more precise instruction.',
    imageGenerationFailedAiServiceUnreachabl: 'Image generation failed (AI service unreachable).',
    mediaGenKindLabelsNotAvailable: '{{MEDIA_GEN_KIND_LABELS}} not available — {{v}}.',
    generationFailedError: 'Generation failed: {{error}}',
    generationFailed: 'Generation failed.',
    generationIsStillInProgressReopen: 'Generation is still in progress. Reopen this media from the media library in a moment.',
    generationFinishedButNoUsableMedia: 'Generation finished but no usable media was returned.',
    mediaGeneratedAndSavedToThe: 'Media generated and saved to the media library.',
    generationFailedAiServiceUnreachable: 'Generation failed (AI service unreachable).',
    generated: 'generated',
    mediaSavedToTheMediaLibrary: 'Media saved to the media library.',
    couldNotSaveTheProviderBlocks: 'Could not save (the provider blocks direct access). Open the media, then download it.',
    generateMediaWithAi: 'Generate media with AI',
    close: 'Close',
    generating2: 'Generating…',
    regenerate: 'Regenerate',
    generate: 'Generate',
    mediaType: 'Media type',
    notAvailableReason: 'Not available — {{reason}}',
    checking: ' · checking…',
    unavailable: ' · unavailable',
    notAvailableV: 'not available — {{v}}.',
    thisCapabilityWillTurnOnAs: 'This capability will turn on as soon as a provider key is configured on the server.',
    instructionPrompt: 'Instruction (prompt)',
    optional: ' — optional',
    eGASmilingSpokespersonHead: 'e.g. a smiling spokesperson, head-and-shoulders shot, studio lighting',
    eGADroneFlyingOver: 'e.g. a drone flying over a misty forest at sunrise',
    eGABrightMinimalistOffice: 'e.g. a bright, minimalist office with a green plant, editorial style',
    sourceImageUrlRequired: 'Source image (URL) — required',
    pasteAnImageUrlEG: 'Paste an image URL (e.g. open a media item from the media library, then “Copy URL”)',
    iConfirmIHaveConsentAnd: 'I confirm I have consent and the rights to the likeness used (no deepfake of a real person without authorization — Law 25).',
    likenessReferenceLicensedUrlOptional: 'Likeness reference (licensed URL) — optional',
    urlOfAProvidedLicensedLikeness: 'URL of a provided / licensed likeness',
    style: 'Style',
    numberOfImagesCount: 'Number of images ({{count}})',
    format: 'Format',
    durationDurationS: 'Duration ({{duration}}s)',
    aiGeneratedImage: 'AI-generated image',
    save: 'Save',
    open: 'Open',
    generatedDescriptions: 'Generated descriptions',
    aiGeneratedMedia: 'AI-generated media',
    savedToTheMediaLibrary: 'Saved to the media library.',
    modelModel: 'Model: {{model}}',
    professional: 'Professional',
    minimalist: 'Minimalist',
    photo: 'Photo',
    illustration: 'Illustration',
    abstract: 'Abstract',
    ai: 'AI',
  },
  imageEditPanel: {
    imageEditedSavedToTheMedia: 'Image edited — saved to the media library.',
    imageEditingFailed: 'Image editing failed.',
    theBrandPipelineProducedNoImage: 'The brand pipeline produced no image.',
    aiEditing: 'AI editing',
    contentStudio: 'content-studio',
    removeTheBackground: 'Remove the background',
    cleanUp: 'Clean up',
    increaseResolutionUpscale: 'Increase resolution (upscale)',
    upscaleFactor: 'Upscale factor',
    brandKit: 'Brand kit',
    loadingKits: 'Loading kits…',
    noBrandKit: 'No brand kit',
    default: ' (default)',
    applyTheBrandKitSColors: 'Apply the brand kit\'s colors / logo / watermark',
    brandPipeline: 'Brand pipeline',
    result: 'Result',
    editedImage: 'Edited image',
    openTheResult: 'Open the result',
    open: 'Open',
    download: 'Download',
    backgroundRemovalUpscalingAndCleanupUse: 'Background removal, upscaling and cleanup use an AI provider (deducted from your credits); a server key is required. The brand pipeline is deterministic and local.',
  },
  mediaLibraryPage: {
    allDates: 'All dates',
    last24H: 'Last 24 h',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    theMediaLibraryIsTemporarilyUnreachable: 'The media library is temporarily unreachable (content-service).',
    failedMediaItemSFailed: '{{failed}} media item(s) failed.',
    nMediaItemSMoved: '{{n}} media item(s) moved.',
    nMediaItemSDeleted: '{{n}} media item(s) deleted.',
    file: 'File',
    type: 'Type',
    folder: 'Folder',
    size: 'Size',
    tags: 'Tags',
    added: 'Added',
    mediaLibrary: 'Media library',
    yourImagesVideosAndFilesOrganized: 'Your images, videos and files — organized, searchable, ready to publish.',
    stockImages: '🖼️ Stock images',
    generateWithAi: '✨ Generate with AI',
    upload: 'Upload',
    media: 'media',
    selected: 'selected',
    folderEmptyNone: 'Folder (empty = none)',
    destinationFolder: 'Destination folder',
    apply: 'Apply',
    deleteSizeMediaItemS: 'Delete {{size}} media item(s)?',
    confirm: 'Confirm',
    cancel: 'Cancel',
    move: 'Move',
    delete: 'Delete',
    erase: 'Erase',
    scheduleTheSelection: 'Schedule the selection',
    files: 'Files',
    storage: 'Storage',
    ofLengthLoaded: 'of {{length}} loaded',
    folders: 'Folders',
    added7D: 'Added (7 d)',
    allFolders: 'All folders',
    searchForAFile: 'Search for a file…',
    searchMedia: 'Search media',
    filterByType: 'Filter by type',
    allTypes: 'All types',
    filterByDate: 'Filter by date',
    filterByTag: 'Filter by tag',
    allTags: 'All tags',
    gallery: 'Gallery',
    list: 'List',
    loadingTheMediaLibrary: 'Loading the media library',
    loading: 'Loading…',
    noMediaMatches: 'No media matches',
    yourMediaLibraryIsEmpty: 'Your media library is empty',
    adjustTheSearchOrFiltersTo: 'Adjust the search or filters to broaden the results.',
    uploadYourFirstFilesOrGenerate: 'Upload your first files or generate an image with AI to get started.',
    ofLengthLoaded2: ' (of {{length}} loaded)',
    openFilename: 'Open {{filename}}',
    selectFilename: 'Select {{filename}}',
    noMedia: 'No media.',
    used: 'used',
    basedOnTheLengthMostRecent: 'Based on the {{length}} most recent files (of {{total}}).',
    noStorageDataToShow: 'No storage data to show.',
    recentAdditions: 'Recent additions',
    over7Days: 'over 7 days',
    importVolumePerDayLast14: 'Import volume per day (last 14 days).',
    aiGeneration: 'AI generation',
    createVisualsFromASimpleInstruction: 'Create visuals from a simple instruction — images are produced by the ai-orchestrator and can be saved to the media library.',
    generateAnImage: '✨ Generate an image',
  },
  mediaUi: {
    justNow: 'just now',
    mMinAgo: '{{m}} min ago',
    hHAgo: '{{h}} h ago',
    dDAgo: '{{d}} d ago',
    noStorageData: 'No storage data.',
  },
  stockMediaDrawer: {
    enterASearchTerm: 'Enter a search term.',
    theSearchFailed: 'The search failed.',
    imageImportedCreditV: 'Image imported (credit: {{v}}).',
    theImportFailed: 'The import failed.',
    stockImages: 'Stock images',
    searchEGTeamOfficeNature: 'Search (e.g. team, office, nature)…',
    searchStockMedia: 'Search stock media',
    orientation: 'Orientation',
    allOrientations: 'All orientations',
    landscape: 'Landscape',
    portrait: 'Portrait',
    square: 'Square',
    searching: 'Searching…',
    search: 'Search',
    resultSVia: 'result(s) via',
    attributionRequiredAuthorSourceKeptOn: '— attribution required (author + source kept on import).',
    noResultsForThisSearch: 'No results for this search.',
    stockResult: 'Stock result',
    creditAuthor: 'Credit: {{author}}',
    importing: 'Importing…',
    import: 'Import',
  },
  uploadModal: {
    uploadFailed: 'Upload failed.',
    okFileSUploaded: '{{ok}} file(s) uploaded.',
    noImageFoundAtThisUrl: 'No image found at this URL.',
    couldNotReadThisUrlPage: 'Could not read this URL (page unreachable or protected).',
    imageImportedIntoTheMediaLibrary: 'Image imported into the media library.',
    automaticImportFailedTheHostBlocks: 'Automatic import failed (the host blocks direct access to the image). Download it, then upload it.',
    folder: 'Folder',
    eGCampaigns: 'e.g. campaigns',
    tagsSeparatedBy: 'Tags (separated by ,)',
    promoSummer: 'promo, summer',
    altText: 'Alt text',
    description: 'Description',
    addMedia: 'Add media',
    close: 'Close',
    uploading: 'Uploading…',
    uploadV: 'Upload{{v}}',
    importing: 'Importing…',
    importIntoTheMediaLibrary: 'Import into the media library',
    files: 'Files',
    fromAUrl: 'From a URL',
    importMode: 'Import mode',
    dragAndDropYourFilesHere: 'Drag and drop your files here',
    imagesVideosGifsAudioOrPdf: 'Images, videos, GIFs, audio or PDF',
    browse: 'Browse…',
    done: 'Done',
    failed: 'Failed',
    queued: 'Queued',
    urlOfAPageOrAn: 'URL of a page or an image',
    httpsExampleComArticle: 'https://example.com/article',
    analyze: 'Analyze',
    imageFound: 'Image found',
  },
};

export default en;
